import { useState, useEffect } from "react";
import { Navigation } from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { 
  Tabs, 
  TabsContent, 
  TabsList, 
  TabsTrigger 
} from "@/components/ui/tabs";
import { ArrowLeft, Upload, Image as ImageIcon, Sparkles, Loader2, BookOpen, Edit, Save, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { motion } from "framer-motion";
import { v4 as uuidv4 } from "uuid";
import { useAiArtGeneration } from "@/hooks/use-ai-art-generation";
import MarkdownIt from "markdown-it";
import MarkdownEditor from "react-markdown-editor-lite";
import "react-markdown-editor-lite/lib/index.css";

const categories = [
  "Fantasia",
  "Ficção Científica",
  "Romance",
  "Terror",
  "Aventura",
  "Drama",
  "Comédia",
  "Mistério",
  "Ação",
  "Histórico"
];

const ageRatings = [
  "Livre",
  "10 a 12 anos",
  "12 a 14 anos",
  "14 a 16 anos",
  "16 a 18 anos"
];

const languages = [
  "Português",
  "Inglês",
  "Espanhol",
  "Francês",
  "Alemão",
  "Italiano",
  "Japonês",
  "Coreano",
  "Chinês"
];

// Initialize markdown parser
const mdParser = new MarkdownIt();

export default function CreateWebnovel() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [ageRating, setAgeRating] = useState("Livre");
  const [language, setLanguage] = useState("Português");
  const [coverImage, setCoverImage] = useState<File | null>(null);
  const [coverPreview, setCoverPreview] = useState("");
  const [userId, setUserId] = useState<string | null>(null);
  const [coverSource, setCoverSource] = useState<"upload" | "ai">("upload");
  
  // Chapter related states
  const [showChapterEditor, setShowChapterEditor] = useState(false);
  const [chapterTitle, setChapterTitle] = useState("Capítulo 1");
  const [chapterContent, setChapterContent] = useState("");
  const [isSavingChapter, setIsSavingChapter] = useState(false);
  const [isChapterSaved, setIsChapterSaved] = useState(false);
  const [isEditingChapter, setIsEditingChapter] = useState(false);
  
  // AI Art generation
  const [prompt, setPrompt] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [showApiKey, setShowApiKey] = useState(false);
  const { isGenerating, generatedImageUrl, error, generateImage, reset } = useAiArtGeneration();

  // Get current user
  useEffect(() => {
    const getUser = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user?.id) {
          setUserId(session.user.id);
        } else {
          toast.error("Você precisa estar logado para criar uma webnovel.");
          navigate("/auth");
        }
      } catch (err) {
        console.error("Error fetching user session:", err);
        toast.error("Erro ao verificar sessão. Tente novamente.");
      }
    };
    
    getUser();
  }, [navigate]);

  // Handle AI generated image
  useEffect(() => {
    if (generatedImageUrl) {
      setCoverPreview(generatedImageUrl);
    }
  }, [generatedImageUrl]);

  const handleCoverChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setCoverImage(file);
      setCoverPreview(URL.createObjectURL(file));
    }
  };

  const handleAiGenerate = async () => {
    if (!prompt) {
      toast.error("Por favor, forneça uma descrição para a capa");
      return;
    }

    if (!apiKey) {
      toast.error("Por favor, forneça uma chave de API da OpenAI");
      return;
    }

    // Add aspect ratio suggestion to the prompt
    const enhancedPrompt = `${prompt} (proporção/ratio 9:16, formato retrato para capa de livro)`;
    await generateImage(enhancedPrompt, apiKey);
  };

  const handleChapterEditorChange = ({ text }: { text: string }) => {
    setChapterContent(text);
  };

  const handleSaveChapter = () => {
    if (!chapterTitle.trim()) {
      toast.error("Por favor, adicione um título para o capítulo");
      return;
    }

    if (!chapterContent.trim()) {
      toast.error("Por favor, adicione conteúdo para o capítulo");
      return;
    }

    setIsSavingChapter(true);
    
    // Simulate saving delay
    setTimeout(() => {
      setIsSavingChapter(false);
      setIsChapterSaved(true);
      setIsEditingChapter(false);
      toast.success("Capítulo salvo com sucesso!");
    }, 800);
  };

  const handleClearChapter = () => {
    setChapterTitle("Capítulo 1");
    setChapterContent("");
    setIsChapterSaved(false);
    setIsEditingChapter(false);
  };

  const handleEditChapter = () => {
    setIsEditingChapter(true);
    setIsChapterSaved(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if ((!coverImage && !generatedImageUrl) || !title || !category || !description) {
      toast.error("Por favor, preencha todos os campos obrigatórios.");
      return;
    }

    setIsLoading(true);
    try {
      let publicUrl = "";
      
      // Handle uploaded image
      if (coverImage) {
        const coverFileName = `${uuidv4()}-${coverImage.name}`;
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('novel-covers')
          .upload(coverFileName, coverImage);

        if (uploadError) {
          console.error("Upload error:", uploadError);
          throw uploadError;
        }

        // Get the public URL of the uploaded image
        const { data: { publicUrl: uploadedUrl } } = supabase.storage
          .from('novel-covers')
          .getPublicUrl(coverFileName);
          
        publicUrl = uploadedUrl;
      } 
      // Use AI generated image URL
      else if (generatedImageUrl) {
        publicUrl = generatedImageUrl;
      }

      // Create webnovel record in the database
      const { data: webnovelData, error: dbError } = await supabase.from("webnovels").insert({
        title,
        description,
        cover_url: publicUrl,
        category,
        age_rating: ageRating,
        language: language,
        author_id: userId
      }).select("id").single();

      if (dbError) {
        console.error("Database error:", dbError);
        throw dbError;
      }

      // If a chapter was created, save it to the database
      if (isChapterSaved && webnovelData?.id) {
        const { error: chapterError } = await supabase.from("chapters").insert({
          webnovel_id: webnovelData.id,
          chapter_number: 1,
          title: chapterTitle,
          content: chapterContent
        });

        if (chapterError) {
          console.error("Chapter creation error:", chapterError);
          // Don't fail the entire process if chapter creation fails
          toast.error("Webnovel criada, mas houve um problema ao salvar o capítulo.");
        }
      }

      toast.success("Webnovel criada com sucesso!");
      navigate("/my-webnovels");
    } catch (error: any) {
      console.error("Erro ao criar webnovel:", error);
      toast.error(error.message || "Ocorreu um erro ao tentar criar sua webnovel. Tente novamente.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCategoryChange = (newCategory: string) => {
    try {
      setCategory(newCategory);
    } catch (error) {
      console.error("Error setting category:", error);
    }
  };

  return (
    <div className="min-h-screen">
      <Navigation />
      <motion.main 
        className="container mx-auto px-4 pt-24 pb-16 max-w-2xl"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <Button
          variant="ghost"
          className="mb-6"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar
        </Button>

        <h1 className="text-2xl font-bold mb-8">Criar nova WebNovel</h1>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Cover Image */}
          <div className="space-y-2">
            <Label>Capa (Proporção 9:16 recomendada)</Label>
            <Tabs 
              defaultValue="upload" 
              value={coverSource} 
              onValueChange={(value) => setCoverSource(value as "upload" | "ai")}
              className="w-full"
            >
              <TabsList className="grid grid-cols-2 mb-4">
                <TabsTrigger value="upload" className="flex items-center gap-1">
                  <Upload size={16} /> Upload
                </TabsTrigger>
                <TabsTrigger value="ai" className="flex items-center gap-1">
                  <Sparkles size={16} /> IA Art
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="upload" className="flex justify-center">
                {coverPreview && coverSource === "upload" ? (
                  <div className="relative w-[225px] h-[400px] rounded-lg overflow-hidden bg-muted">
                    <img 
                      src={coverPreview} 
                      alt="Cover preview" 
                      className="w-full h-full object-cover"
                    />
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      className="absolute bottom-2 right-2"
                      onClick={() => {
                        setCoverPreview("");
                        setCoverImage(null);
                      }}
                    >
                      Alterar
                    </Button>
                  </div>
                ) : (
                  <label htmlFor="cover" className="flex flex-col items-center justify-center w-[225px] h-[400px] border-2 border-dashed border-muted-foreground/25 rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
                    <ImageIcon className="h-10 w-10 mb-2 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Clique para adicionar</span>
                    <span className="text-xs text-muted-foreground">Proporção 9:16 recomendada</span>
                    <input 
                      id="cover" 
                      type="file" 
                      accept="image/*" 
                      className="hidden" 
                      onChange={handleCoverChange} 
                      disabled={isLoading}
                    />
                  </label>
                )}
              </TabsContent>
              
              <TabsContent value="ai" className="space-y-4">
                {generatedImageUrl && coverSource === "ai" ? (
                  <div className="flex justify-center">
                    <div className="relative w-[225px] h-[400px] rounded-lg overflow-hidden bg-muted">
                      <img 
                        src={generatedImageUrl} 
                        alt="IA Generated Cover" 
                        className="w-full h-full object-cover"
                      />
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        className="absolute bottom-2 right-2"
                        onClick={() => {
                          reset();
                          setCoverPreview("");
                        }}
                      >
                        Alterar
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="prompt">Descrição da capa</Label>
                      <Textarea
                        id="prompt"
                        placeholder="Descreva a capa que você deseja gerar. Ex: Uma capa de webnovel de fantasia com um castelo flutuante"
                        value={prompt}
                        onChange={(e) => setPrompt(e.target.value)}
                        disabled={isGenerating || isLoading}
                        className="h-24"
                      />
                      <p className="text-xs text-amber-600">
                        <strong>Importante:</strong> A imagem será gerada em proporção 9:16 (formato retrato). 
                        Se a imagem gerada não seguir esta proporção, ela será cortada para se adequar.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <Label htmlFor="apiKey">Chave da API OpenAI</Label>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => setShowApiKey(!showApiKey)}
                          type="button"
                        >
                          {showApiKey ? "Ocultar" : "Mostrar"}
                        </Button>
                      </div>
                      <Input
                        id="apiKey"
                        type={showApiKey ? "text" : "password"}
                        placeholder="sk-..."
                        value={apiKey}
                        onChange={(e) => setApiKey(e.target.value)}
                        disabled={isGenerating || isLoading}
                      />
                      <p className="text-xs text-muted-foreground">
                        Sua chave será usada apenas para gerar esta imagem e não será armazenada.
                        <a 
                          href="https://platform.openai.com/api-keys" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-primary hover:underline ml-1"
                        >
                          Obter uma chave API
                        </a>
                      </p>
                    </div>

                    {error && (
                      <div className="text-sm text-destructive p-2 border border-destructive/50 rounded-md bg-destructive/10">
                        {error}
                      </div>
                    )}

                    <Button 
                      type="button" 
                      onClick={handleAiGenerate} 
                      disabled={isGenerating || !prompt || !apiKey || isLoading} 
                      className="w-full"
                    >
                      {isGenerating ? (
                        <>Gerando imagem...</>
                      ) : (
                        <>
                          <Sparkles className="h-4 w-4 mr-2" /> 
                          Gerar capa com IA
                        </>
                      )}
                    </Button>
                  </>
                )}
              </TabsContent>
            </Tabs>
          </div>

          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">Título</Label>
            <Input 
              id="title" 
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Digite o título da sua webnovel"
              disabled={isLoading}
              required
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Descrição</Label>
            <Textarea 
              id="description" 
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Escreva uma breve descrição da sua webnovel"
              rows={4}
              disabled={isLoading}
              required
            />
          </div>

          {/* Category */}
          <div className="space-y-2">
            <Label htmlFor="category">Categoria</Label>
            <Select
              value={category}
              onValueChange={handleCategoryChange}
              disabled={isLoading}
            >
              <SelectTrigger id="category">
                <SelectValue placeholder="Selecione uma categoria" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((cat) => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Age Rating */}
          <div className="space-y-2">
            <Label htmlFor="ageRating">Classificação Indicativa</Label>
            <Select
              value={ageRating}
              onValueChange={setAgeRating}
              disabled={isLoading}
            >
              <SelectTrigger id="ageRating">
                <SelectValue placeholder="Selecione uma classificação etária" />
              </SelectTrigger>
              <SelectContent>
                {ageRatings.map((rating) => (
                  <SelectItem key={rating} value={rating}>{rating}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Language */}
          <div className="space-y-2">
            <Label htmlFor="language">Idioma</Label>
            <Select
              value={language}
              onValueChange={setLanguage}
              disabled={isLoading}
            >
              <SelectTrigger id="language">
                <SelectValue placeholder="Selecione um idioma" />
              </SelectTrigger>
              <SelectContent>
                {languages.map((lang) => (
                  <SelectItem key={lang} value={lang}>{lang}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Add Chapter Button */}
          <div className="pt-4">
            <Button
              type="button"
              variant={showChapterEditor ? "secondary" : "outline"}
              className="w-full"
              onClick={() => setShowChapterEditor(!showChapterEditor)}
              disabled={isLoading}
            >
              <BookOpen className="h-4 w-4 mr-2" />
              {showChapterEditor ? "Ocultar Editor de Capítulo" : "Informar Capítulo"}
            </Button>
          </div>

          {/* Chapter Editor */}
          {showChapterEditor && (
            <motion.div 
              className="space-y-4 border border-border rounded-lg p-4 bg-card/50"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              transition={{ duration: 0.3 }}
            >
              <div className="space-y-2">
                <Label htmlFor="chapterTitle">Título do Capítulo</Label>
                <Input 
                  id="chapterTitle" 
                  value={chapterTitle}
                  onChange={(e) => setChapterTitle(e.target.value)}
                  placeholder="Título do capítulo"
                  disabled={isLoading || (isChapterSaved && !isEditingChapter)}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="chapterContent">Conteúdo do Capítulo</Label>
                <div className="border rounded-md overflow-hidden">
                  <MarkdownEditor
                    value={chapterContent}
                    style={{ height: '400px' }}
                    onChange={handleChapterEditorChange}
                    renderHTML={(text) => mdParser.render(text)}
                  />
                </div>
              </div>

              <div className="flex gap-2 justify-end pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClearChapter}
                  disabled={isLoading || (!chapterContent && !chapterTitle)}
                >
                  <X className="h-4 w-4 mr-2" />
                  Limpar
                </Button>

                {isChapterSaved && !isEditingChapter ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleEditChapter}
                    disabled={isLoading}
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Editar
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={handleSaveChapter}
                    disabled={isLoading || isSavingChapter || !chapterContent || !chapterTitle}
                    className="group relative disabled:opacity-80"
                  >
                    <span className={isSavingChapter ? "text-transparent" : ""}>
                      Salvar Capítulo
                    </span>
                    {isSavingChapter && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Loader2 className="h-4 w-4 animate-spin" />
                      </div>
                    )}
                    {!isSavingChapter && <Save className="h-4 w-4 ml-2" />}
                  </Button>
                )}
              </div>

              {isChapterSaved && !isEditingChapter && (
                <div className="bg-green-100 dark:bg-green-900/20 p-3 rounded-md text-sm text-green-800 dark:text-green-300">
                  Capítulo salvo e será criado junto com a webnovel.
                </div>
              )}
            </motion.div>
          )}

          {/* Submit Button */}
          <Button 
            type="submit" 
            className="w-full group relative disabled:opacity-90"
            disabled={isLoading || (!coverImage && !generatedImageUrl)}
          >
            <span className={isLoading ? "text-transparent" : ""}>
              Criar WebNovel
            </span>
            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            )}
          </Button>
        </form>
      </motion.main>
    </div>
  );
}
